interface AnswerItem {
  question: string;
  answer: string;
  bullets?: string[];
  icon: string;
}

interface AIAnswerBlockProps {
  heading?: string;
  intro?: string;
  items?: AnswerItem[];
}

const defaultItems: AnswerItem[] = [
  {
    icon: "ri-tools-line",
    question: "What Services Are Offered?",
    answer: "Thomas Roofing & Repair provides a full range of residential roofing solutions designed to protect your home from Colorado's extreme weather conditions.",
    bullets: [
      "Roof Repair — leak fixes, shingle replacement, flashing repair",
      "Full Roof Replacement — complete tear-off and installation",
      "Roof Inspections — comprehensive condition assessments",
      "Storm & Hail Damage Repair — emergency response services",
      "Leak Detection & Waterproofing — precision moisture diagnosis",
    ],
  },
  {
    icon: "ri-checkbox-circle-line",
    question: "What Are the Benefits?",
    answer: "Homeowners who work with Thomas Roofing get measurable, lasting improvements to their home's safety and value.",
    bullets: [
      "Permanent leak resolution — stop water damage for good",
      "Improved energy efficiency with modern roofing materials",
      "Higher property value and curb appeal",
      "Peace of mind with licensed, insured professionals",
      "Long-term protection backed by workmanship warranty",
    ],
  },
  {
    icon: "ri-route-line",
    question: "What Is the Process?",
    answer: "Every project follows a transparent, 5-step process designed to eliminate surprises and deliver quality results on time.",
    bullets: [
      "Step 1: Free inspection — we assess every inch of your roof",
      "Step 2: Honest diagnosis — no upsells, just facts",
      "Step 3: Clear written estimate — transparent pricing upfront",
      "Step 4: Professional execution — skilled crew, quality materials",
      "Step 5: Final quality check — we don't leave until it's perfect",
    ],
  },
];

export default function AIAnswerBlock({
  heading = "What Does Thomas Roofing & Repair Do?",
  intro = "Thomas Roofing & Repair Inc. is Central Florida's trusted residential roofing company, serving the Orlando area and beyond with repairs, replacements, inspections, and emergency storm damage restoration. Licensed (#CCC1333138) and fully insured, the company protects homes from leaks, storm damage, and structural deterioration using premium materials — delivering results built to handle Florida's demanding climate.",
  items = defaultItems,
}: AIAnswerBlockProps) {
  return (
    <section className="bg-[#F8F9FA] py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mb-14">
          <span className="text-[#10B981] font-body font-600 text-sm tracking-widest uppercase mb-3 block">
            About Our Services
          </span>
          <h2 className="font-headline font-800 text-[#1A1A1A] text-3xl lg:text-4xl xl:text-5xl mb-6 leading-tight">
            {heading}
          </h2>
          <p className="text-[#6B7280] font-body text-base lg:text-lg leading-relaxed border-l-4 border-[#10B981] pl-5">
            {intro}
          </p>
        </div>

        {/* Answer Blocks */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {items.map((item, idx) => (
            <div
              key={idx}
              className="bg-white rounded-lg p-7 border border-gray-100 hover:border-[#10B981]/30 transition-all duration-200"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#1A1A1A]">
                  <i className={`${item.icon} text-[#10B981] text-lg`}></i>
                </div>
                <h3 className="font-headline font-700 text-[#1A1A1A] text-base leading-tight">{item.question}</h3>
              </div>
              <p className="text-[#6B7280] font-body text-sm leading-relaxed mb-4">{item.answer}</p>
              {item.bullets && (
                <ul className="flex flex-col gap-2">
                  {item.bullets.map((bullet, bi) => (
                    <li key={bi} className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-checkbox-circle-fill text-[#10B981] text-xs"></i>
                      </div>
                      <span className="text-[#1A1A1A] font-body text-sm">{bullet}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
